import { api, HttpError, type CaptureCounts } from './api';
import { confirmed, currentOwner, onSession, PERSONAL_OWNER, resolveSession, session, type Identity } from './session';
import type { Allowance, Moment } from './types';
import type { ResearchAttempt } from './research/delivery';

/**
 * Private device drafts (ticket 12; the offline outbox before it).
 *
 * A capture that cannot become a lesson right now — no connection, a server
 * hiccup, or a refusal the learner can wait out (today's new moments used up,
 * today's review not done, the month's AI allowance spent) — is kept in
 * IndexedDB on this device (Blobs survive reloads and PWA restarts) and sent
 * later, oldest first, one at a time.
 *
 * Five states, and the screen says which one a draft is in (ticket 33):
 * `local` (on this device only — nothing has been accepted yet), `blocked`
 * (the server refused for a limit that lifts, and `resetsAt` says when),
 * `sending` (a request is in flight right now, never stored), `failed` (it
 * cannot become a lesson as it stands) and completed (it became one). Every
 * row offers the action its state allows, named: "Send now", "Check now",
 * "Review now", "Open", "Delete".
 *
 * Sending is never promised for a time the app is not there for. It happens
 * while this app is open and online, signed in as the account the draft
 * belongs to: when a session is confirmed, when the connection comes back,
 * when the moment a blocked draft waits for arrives (a timer that only runs
 * while the app is open), and when the learner finishes the day's review. A
 * closed or offline app sends nothing, whatever the clock says; the draft is
 * still here when it opens.
 *
 * Each draft belongs to the account and target language it was captured
 * under. Only the signed-in learner's own drafts are listed, counted or sent;
 * everyone else's stay untouched and invisible until their owner signs in
 * again here. The server double-checks the binding on every upload.
 *
 * Retries are idempotent: the draft id is the request's `clientId`, so an
 * ambiguous acknowledgement — the server made the moment and the answer never
 * arrived — is checked by that id first. If the moment is already there it is
 * reopened; if it is absent, replaying the same idempotent create request
 * returns that moment without another daily slot or charge. A row left behind
 * by a device that could not delete it is checked the same way on the next
 * run, and a moment that was made and then deleted is never made again.
 *
 * Beta keeps an original recording for 30 days from capture (ticket 13), and
 * a device draft is no exception: one older than that is never sent. It is
 * removed from this device the next time the drafts run here — when the app
 * opens or its drafts are loaded, never while the app is closed or the device
 * is off — and the learner is told once how many went.
 */

export const MAX_DRAFTS = 5;
export const MAX_AUDIO_BYTES = 15 * 1024 * 1024;
export const MAX_TEXT_CHARS = 2000;
/** Beta: a draft (like the recording it would become) is kept this long from capture. */
export const DRAFT_MAX_AGE_DAYS = 30;
const DRAFT_MAX_AGE_MS = DRAFT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

export type DraftKind = 'audio' | 'text';
/**
 * Stored lifecycle. `local` is on this device only — nothing was accepted yet
 * (a capture saved with no connection, or one whose send was interrupted);
 * `blocked` waits for a limit the server named; `failed` cannot become a
 * lesson as it stands. "Sending" is not stored: it exists only while a request
 * is in flight (see `DraftsView.sendingId`).
 */
export type DraftState = 'local' | 'blocked' | 'failed';
/** The five states the screen shows: the stored three, the in-flight one, and a draft that became a lesson. */
export type DraftUiState = DraftState | 'sending' | 'completed';
export type BlockedReason = 'daily-allowance' | 'daily-review' | 'ai-allowance' | 'beta-budget';
export type FailReason = 'draft-account' | 'draft-language' | 'too-large' | 'rejected';
/** Why a draft is waiting, and when that lifts, if the server said. */
export type DraftBlock = { reason: BlockedReason; resetsAt?: string };
/** What `saveDraft` should record about why a capture became a draft. */
export type DraftSaveOpts = { blocked?: DraftBlock; lastError?: string; ambiguous?: boolean };

/**
 * What the one capture button adds to a capture (ticket 44): the holder's note spans, the counts sent with the make
 * request, a retold scene, and `unheard` for a clip that could not be heard offline (it waits for the lantern).
 * Never a token: the held clip is gone by the time a draft is tried, and the device's copy is uploaded instead.
 */
export interface CaptureExtra {
  spans?: { start: number; end: number }[];
  capture?: CaptureCounts;
  retell?: boolean;
  unheard?: boolean;
  /** A voice message shared to Eaves (kept in a draft, it is still sent as one). */
  shared?: boolean;
}

export interface Draft {
  /** Client-generated id, also sent as `clientId` (the stable logical request id). */
  id: string;
  /** Beta: the account id at capture. Personal mode: `'personal'`. */
  owner: string;
  /** Target and explanation language at capture ('' for drafts older than this). */
  target: string;
  explain: string;
  kind: DraftKind;
  blob?: Blob;
  mime?: string;
  /** Audio: optional context note. */
  note?: string;
  /** Text: the "wanted to say" text. */
  wanted?: string;
  /** Text: the newcomer moment whose question it answers (v1 ticket 42). */
  answer?: string;
  durationSec?: number;
  extra?: CaptureExtra;
  /** Content-free origin needed to report a later retry against the original attempt. */
  researchAttempt?: ResearchAttempt;
  createdAt: number;
  state: DraftState;
  /** How many creation requests this device has made for it. */
  attempts: number;
  lastError?: string;
  blockedReason?: BlockedReason;
  /** When the blocking reason lifts, if the server said. */
  resetsAt?: string;
  /**
   * A creation request was sent and its answer never arrived, so the server
   * may hold the moment already: the next request for this id either resumes
   * that moment or makes the first one. Cleared only after the server confirms
   * its status.
   */
  ambiguous?: true;
  failReason?: FailReason;
}

/** What the learner captured, before it is a draft. */
export interface Capture {
  clientId: string;
  kind: DraftKind;
  blob?: Blob;
  note?: string;
  wanted?: string;
  /** Text: the newcomer moment whose question it answers (v1 ticket 42). */
  answer?: string;
  durationSec?: number;
  /** When it was captured (ms); the draft's age, and the recording's retention, count from here. */
  capturedAt?: number;
  extra?: CaptureExtra;
  researchAttempt?: ResearchAttempt;
  /** Ticket 44: the clip the server holds; used by the request in hand, never kept in a draft. */
  token?: string;
}

/** A draft that became a lesson (or turned out to be one already deleted) this session. */
export interface CompletedDraft {
  id: string;
  owner: string;
  kind: DraftKind;
  momentId?: string;
  /** The lesson had been made and then deleted: nothing was sent again. */
  removed?: boolean;
  researchAttempt?: ResearchAttempt;
}

export type SaveProblem = 'full' | 'too-large' | 'too-long' | 'no-owner' | 'storage';

export class DraftSaveError extends Error {
  constructor(public code: SaveProblem, message?: string) {
    super(message ?? code);
    this.name = 'DraftSaveError';
  }
}

const DB = 'learnimmerse-outbox';
/** The object store keeps its v1 name; the states it holds are `DraftState`'s. */
const STORE = 'pending';
const BY_OWNER = 'owner';

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function makeClientId(): string {
  return newId();
}

/** Network-level failure (the browser never got an HTTP response). */
export function isNetworkError(e: unknown): boolean {
  if (typeof navigator !== 'undefined' && 'onLine' in navigator && navigator.onLine === false) return true;
  const msg = e instanceof Error ? `${e.name}: ${e.message}` : String(e ?? '');
  return e instanceof TypeError || /failed to fetch|networkerror|load failed|network request failed|offline/i.test(msg);
}

export type Outcome =
  | ({ kind: 'blocked' } & DraftBlock)
  | { kind: 'failed'; reason: FailReason; message: string }
  | { kind: 'removed' }
  | { kind: 'expired' }
  | { kind: 'session' }
  | { kind: 'offline'; message: string }
  | { kind: 'transient'; message: string };

/**
 * What asking the server about one draft's logical request says, without
 * creating anything. `sent` means a moment for this id exists there already —
 * an ambiguous acknowledgement, reconciled.
 */
export type Reconciliation =
  | { kind: 'sent'; momentId: string }
  | ({ kind: 'blocked' } & DraftBlock)
  | { kind: 'failed'; reason: FailReason; message: string }
  | { kind: 'removed' }
  | { kind: 'expired' }
  | { kind: 'session' }
  | { kind: 'offline'; message: string };

/** A stored draft without its payload (never expected; it cannot be sent). */
class DraftPayloadMissing extends Error {}

const message = (e: unknown) => (e instanceof Error ? e.message : String(e ?? 'upload failed'));

/** What a failed creation request means for the capture behind it. */
export function outcomeOf(e: unknown): Outcome {
  if (!(e instanceof HttpError)) {
    if (e instanceof DraftPayloadMissing) return { kind: 'failed', reason: 'rejected', message: e.message };
    return isNetworkError(e) ? { kind: 'offline', message: message(e) } : { kind: 'transient', message: message(e) };
  }
  const reason = e.body?.reason;
  const resetsAt = typeof e.body?.resetsAt === 'string' ? e.body.resetsAt : undefined;
  if (e.status === 409 && reason === 'daily-review') return { kind: 'blocked', reason, resetsAt };
  if (e.status === 429 && reason === 'daily-allowance') return { kind: 'blocked', reason, resetsAt };
  if (e.status === 402 && (reason === 'ai-allowance' || reason === 'beta-budget')) return { kind: 'blocked', reason, resetsAt };
  if (e.status === 409 && (reason === 'draft-account' || reason === 'draft-language')) return { kind: 'failed', reason, message: e.message };
  if (e.status === 409 && reason === 'draft-expired') return { kind: 'expired' };
  if (e.status === 410) return { kind: 'removed' };
  if (e.status === 401) return { kind: 'session' };
  if (e.status === 413) return { kind: 'failed', reason: 'too-large', message: e.message };
  if (e.status === 408 || e.status === 429 || e.status >= 500) return { kind: 'transient', message: e.message };
  return { kind: 'failed', reason: 'rejected', message: e.message };
}

/** The same refusals, read off the ask-the-server path (nothing was created). */
function reconcileOf(e: unknown): Reconciliation {
  const o = outcomeOf(e);
  return o.kind === 'transient' ? { kind: 'offline', message: o.message } : o;
}

/**
 * What the last allowance read says would stop a new moment right now, in
 * the server's own order (review, then AI budget, then today's slots).
 */
export function blockOf(a?: Allowance): DraftBlock | null {
  if (!a || a.limit === null) return null;
  if (a.review?.status === 'pending') return { reason: 'daily-review', resetsAt: a.review.resetsAt };
  if (a.ai?.exhausted) return { reason: a.ai.reason === 'beta-budget' ? 'beta-budget' : 'ai-allowance', resetsAt: a.ai.resetsAt };
  if (a.remaining <= 0) return { reason: 'daily-allowance', resetsAt: a.resetsAt };
  return null;
}

/** Could this blocked draft's reason have cleared, going by one allowance read? */
function cleared(d: Draft, a: Allowance | undefined): boolean {
  if (!a) return false;
  if (a.limit === null) return true;
  switch (d.blockedReason) {
    case 'daily-review': return a.review?.status !== 'pending';
    case 'daily-allowance': return a.remaining > 0;
    case 'ai-allowance':
    case 'beta-budget': return !a.ai?.exhausted;
    default: return true;
  }
}

// --- IndexedDB (no dependency) ---

/** Drafts stored before ticket 12 had no owner or lifecycle: they are the personal learner's, waiting. */
function normalise(raw: Partial<Draft> & { id: string; state?: string }): Draft {
  const state = raw.state === 'blocked' || raw.state === 'failed' ? raw.state : 'local';
  return {
    ...raw,
    owner: raw.owner || PERSONAL_OWNER,
    target: raw.target ?? '',
    explain: raw.explain ?? '',
    kind: raw.kind === 'text' ? 'text' : 'audio',
    createdAt: raw.createdAt ?? 0,
    // `pending` is what ticket 12 wrote for a capture this device holds and the
    // server has not accepted: the same state `local` names now (ticket 33).
    state,
    attempts: raw.attempts ?? 0,
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // v2 (ticket 12): owner index, and every v1 entry migrated in place.
    const req = indexedDB.open(DB, 2);
    req.onupgradeneeded = (ev) => {
      const db = req.result;
      const t = req.transaction!;
      const store = db.objectStoreNames.contains(STORE) ? t.objectStore(STORE) : db.createObjectStore(STORE, { keyPath: 'id' });
      if (!store.indexNames.contains(BY_OWNER)) store.createIndex(BY_OWNER, 'owner');
      if (ev.oldVersion >= 1 && ev.oldVersion < 2) {
        const cursor = store.openCursor();
        cursor.onsuccess = () => {
          const c = cursor.result;
          if (!c) return;
          c.update(normalise(c.value as Draft));
          c.continue();
        };
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // A newer version in another tab must not be blocked by this handle.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error('IndexedDB unavailable'));
  });
}

/**
 * One transaction; resolves only on commit (request-success fires first, and
 * a crash in between would lose the write — the loss drafts exist to prevent).
 */
function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore, t: IDBTransaction) => (() => T) | void): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        let settled = false;
        let read: (() => T) | void;
        const close = () => {
          try {
            db.close();
          } catch { /* already closed */ }
        };
        const fail = (err: unknown) => {
          if (settled) return;
          settled = true;
          close();
          reject(err instanceof Error || err instanceof DOMException ? err : new Error('draft transaction failed'));
        };
        const t = db.transaction(STORE, mode);
        t.oncomplete = () => {
          if (settled) return;
          settled = true;
          close();
          try {
            resolve(read ? read() : (undefined as T));
          } catch (e) {
            reject(e);
          }
        };
        t.onerror = () => fail(t.error ?? new Error('draft transaction failed'));
        t.onabort = () => fail(t.error ?? new Error('draft transaction aborted'));
        try {
          read = fn(t.objectStore(STORE), t);
        } catch (e) {
          try {
            t.abort();
          } catch { /* already finished */ }
          fail(e);
        }
      }),
  );
}

/** One owner's drafts, oldest first. Nobody else's are ever read. */
function listOwn(owner: string): Promise<Draft[]> {
  if (typeof indexedDB === 'undefined') return Promise.resolve([]);
  return tx('readonly', (s) => {
    const req = s.index(BY_OWNER).getAll(owner);
    return () => (req.result as Draft[]).map(normalise).sort((a, b) => a.createdAt - b.createdAt);
  });
}

function getDraft(id: string): Promise<Draft | undefined> {
  return tx('readonly', (s) => {
    const req = s.get(id);
    return () => (req.result ? normalise(req.result as Draft) : undefined);
  });
}

/** Update a stored draft in place (a no-op if it is gone). */
function patchDraft(id: string, patch: (d: Draft) => Draft): Promise<void> {
  return tx('readwrite', (s) => {
    const req = s.get(id);
    req.onsuccess = () => {
      if (req.result) s.put(patch(normalise(req.result as Draft)));
    };
  });
}

function removeRow(id: string): Promise<void> {
  return tx('readwrite', (s) => {
    s.delete(id);
  });
}

/**
 * Remove a stored row, but never the one whose send may have started since
 * the caller decided to remove it — re-checked right here, as late as
 * possible before the delete (ticket 12's review fix, c3cb0aa).
 */
async function removeUnlessSending(id: string): Promise<boolean> {
  if (id === sendingId) return false;
  await removeRow(id);
  return true;
}

// --- saving ---

/**
 * Keep a capture on this device for its owner. Refuses (without losing
 * anything: the caller still holds the capture) when the owner already has
 * the maximum, when it is over the size limits, or when storage fails.
 */
export async function saveDraft(
  capture: Capture,
  owner: Identity | null,
  opts: DraftSaveOpts = {},
): Promise<Draft> {
  if (!owner) throw new DraftSaveError('no-owner');
  if (capture.kind === 'audio' && (!capture.blob || capture.blob.size > MAX_AUDIO_BYTES)) throw new DraftSaveError('too-large');
  const text = capture.kind === 'text' ? capture.wanted ?? '' : capture.note ?? '';
  if (text.length > MAX_TEXT_CHARS) throw new DraftSaveError('too-long');
  if (typeof indexedDB === 'undefined') throw new DraftSaveError('storage', 'device storage is unavailable');
  const draft: Draft = {
    id: capture.clientId,
    owner: owner.owner,
    target: owner.target,
    explain: owner.explain,
    kind: capture.kind,
    ...(capture.kind === 'audio'
      ? { blob: capture.blob, mime: capture.blob?.type, note: capture.note, durationSec: capture.durationSec }
      : { wanted: capture.wanted, ...(capture.answer ? { answer: capture.answer } : {}) }),
    ...(capture.extra ? { extra: capture.extra } : {}),
    ...(capture.researchAttempt ? { researchAttempt: capture.researchAttempt } : {}),
    createdAt: capture.capturedAt ?? Date.now(),
    state: opts.blocked ? 'blocked' : 'local',
    attempts: opts.lastError || opts.blocked ? 1 : 0,
    ...(opts.lastError ? { lastError: opts.lastError } : {}),
    ...(opts.blocked ? { blockedReason: opts.blocked.reason, resetsAt: opts.blocked.resetsAt } : {}),
    ...(opts.ambiguous ? { ambiguous: true } : {}),
  };
  let full = false;
  try {
    await tx('readwrite', (s) => {
      const existing = s.get(draft.id);
      existing.onsuccess = () => {
        // Saving the same capture again replaces it and does not count twice.
        if (existing.result) {
          s.put(draft);
          return;
        }
        const count = s.index(BY_OWNER).count(draft.owner);
        count.onsuccess = () => {
          if (count.result >= MAX_DRAFTS) full = true;
          else s.put(draft);
        };
      };
    });
  } catch (e) {
    throw new DraftSaveError('storage', message(e));
  }
  if (full) throw new DraftSaveError('full');
  await notify();
  return draft;
}

/**
 * Delete a draft from this device only — no request is ever made. Never the
 * one being sent right now, and never anyone else's.
 */
export async function deleteDraft(id: string): Promise<boolean> {
  if (id === sendingId) return false;
  const owner = currentOwner();
  const d = await getDraft(id).catch(() => undefined);
  if (!d || !owner || d.owner !== owner) return false;
  // A send for this exact draft may have started while getDraft() awaited.
  if (!(await removeUnlessSending(id))) return false;
  await notify();
  return true;
}

/** Sign-out with "delete my drafts": every draft of the signed-in learner, after any send in flight. */
export async function deleteOwnDrafts(): Promise<number> {
  await whenIdle();
  const owner = currentOwner();
  if (!owner) return 0;
  const mine = await listOwn(owner);
  for (const d of mine) await removeRow(d.id);
  await notify();
  return mine.length;
}

// --- what the screens see ---

export interface DraftsView {
  /** Null until the session is known: nothing may be shown. */
  owner: string | null;
  drafts: Draft[];
  /** IndexedDB could not confirm the current list; screens must not treat it as empty. */
  readError: boolean;
  /** The draft whose request is in flight right now. */
  sendingId: string | null;
  completed: CompletedDraft[];
  max: number;
  /** Beta: how many of this learner's drafts were removed for being over 30 days old (shown once). */
  expired: number;
}

let sendingId: string | null = null;
const completed: CompletedDraft[] = [];
// Keep an accepted draft visibly uncertain in this page if its local row
// could not be deleted. A reload can still reconcile using the same id.
const uncertainIds = new Set<string>();
/**
 * Expired drafts removed this session, per owner, until the notice is
 * dismissed. Ids, not a tally: the load and the send loop can both find the
 * same old row, and it must be counted once.
 */
const expiredIds = new Map<string, Set<string>>();
const countExpired = (owner: string, id: string) => {
  const ids = expiredIds.get(owner) ?? new Set<string>();
  ids.add(id);
  expiredIds.set(owner, ids);
};
let view: DraftsView = { owner: null, drafts: [], readError: false, sendingId: null, completed: [], max: MAX_DRAFTS, expired: 0 };
const listeners = new Set<(v: DraftsView) => void>();
let readGen = 0;
let timer = 0;

export function draftsView(): DraftsView {
  return view;
}

/** Read the current owner's device drafts and wait until subscribers see that read. */
export function refreshDrafts(): Promise<DraftsView> {
  return notify();
}

export function subscribeDrafts(fn: (v: DraftsView) => void): () => void {
  listeners.add(fn);
  fn(view);
  return () => {
    listeners.delete(fn);
  };
}

/** Hide the "drafts older than 30 days were removed" notice. */
export function dismissExpired() {
  if (view.owner) expiredIds.delete(view.owner);
  publish({ ...view, expired: 0 });
}

const isExpired = (d: Draft, now: number) => d.createdAt > 0 && now - d.createdAt >= DRAFT_MAX_AGE_MS;

/**
 * Beta: remove this learner's drafts that are 30 days old or more (never the
 * one in flight) and count them for the notice. Uses this device's clock —
 * the only one there is offline — and runs only when the drafts do.
 */
async function dropExpired(owner: string, drafts: Draft[]): Promise<Draft[]> {
  const s = session();
  if (s.status !== 'known' || !s.beta || s.owner !== owner) return drafts;
  const now = Date.now();
  const kept: Draft[] = [];
  for (const d of drafts) {
    if (!isExpired(d, now) || !(await removeUnlessSending(d.id))) {
      kept.push(d);
      continue;
    }
    countExpired(owner, d.id);
  }
  return kept;
}

/** Hide a "became a lesson" notice. */
export function dismissCompleted(id: string) {
  const i = completed.findIndex((c) => c.id === id);
  if (i >= 0) completed.splice(i, 1);
  publish({ ...view, completed: completed.filter((c) => c.owner === view.owner) });
}

/** A moment's landing has shown it (v1 ticket 41): hide the "became a lesson" notice of the draft it came from. */
export function dismissCompletedMoment(momentId: string) {
  for (const c of completed.filter((x) => x.momentId === momentId)) dismissCompleted(c.id);
}

function publish(next: DraftsView) {
  view = next;
  for (const fn of listeners) {
    try {
      fn(view);
    } catch { /* a broken view must not break the drafts */ }
  }
}

async function notify(): Promise<DraftsView> {
  const gen = ++readGen;
  const owner = currentOwner();
  let drafts: Draft[] = [];
  let readError = false;
  if (owner) {
    try {
      drafts = await dropExpired(owner, await listOwn(owner));
    } catch {
      readError = true;
      // Keep the last confirmed list for the same account. A failed read must
      // never turn a saved clip into an apparent empty outbox.
      drafts = view.owner === owner ? view.drafts : [];
    }
  }
  // A newer read (or a session change) superseded this one. Expiry cleanup is
  // a side effect, though: if it completed in this stale read, make one fresh
  // read so its one-time notice cannot be lost behind the newer request.
  if (gen !== readGen || owner !== currentOwner()) {
    const expired = owner ? expiredIds.get(owner)?.size ?? 0 : 0;
    if (owner && owner === currentOwner() && expired > view.expired) void notify();
    return view;
  }
  publish({
    owner,
    drafts: drafts.map((d) => uncertainIds.has(d.id) ? { ...d, ambiguous: true } : d),
    readError, sendingId, completed: completed.filter((c) => c.owner === owner), max: MAX_DRAFTS,
    expired: owner ? expiredIds.get(owner)?.size ?? 0 : 0,
  });
  if (!readError) armTimer(drafts);
  return view;
}

/** Wake up when the earliest blocked draft's reason lifts (checked against the server, never assumed). */
function armTimer(drafts: Draft[]) {
  if (typeof window === 'undefined') return;
  window.clearTimeout(timer);
  const now = Date.now();
  const due = drafts
    .filter((d) => d.state === 'blocked' && d.resetsAt)
    .map((d) => Date.parse(d.resetsAt!))
    .filter((at) => Number.isFinite(at) && at > now);
  if (due.length === 0) return;
  const delay = Math.min(Math.min(...due) - now + 2000, 6 * 3600 * 1000);
  timer = window.setTimeout(() => void sendDrafts(), delay);
}

// --- sending ---

/** Create the lesson for a capture, bound to the account and language it was made under. */
export function sendCapture(capture: Capture, owner: Identity | null): Promise<Moment> {
  const binding = owner ? { owner: owner.owner, target: owner.target || undefined } : undefined;
  if (capture.kind === 'text') {
    if (!capture.wanted) return Promise.reject(new DraftPayloadMissing('the saved text is empty'));
    return api.createTextMoment(capture.wanted, capture.clientId, binding, capture.answer, {
      retell: capture.extra?.retell, capture: capture.extra?.capture, researchAttempt: capture.researchAttempt,
    });
  }
  if (!capture.blob) return Promise.reject(new DraftPayloadMissing('the saved recording is missing'));
  const opts = {
    note: capture.note, durationSec: capture.durationSec, clientId: capture.clientId, draft: binding, capturedAt: capture.capturedAt,
    spans: capture.extra?.spans, capture: capture.extra?.capture, shared: capture.extra?.shared,
    researchAttempt: capture.researchAttempt,
  };
  // The one capture button makes from the clip the server holds; a lapsed token (410 hear-gone) falls back to the device's copy.
  if (!capture.token) return api.createMoment(capture.blob, opts);
  return api.createMoment(null, { ...opts, token: capture.token }).catch((e) => {
    if (e instanceof HttpError && e.status === 410 && e.code === 'hear-gone') return api.createMoment(capture.blob!, opts);
    throw e;
  });
}

function captureOf(d: Draft): Capture {
  return { clientId: d.id, kind: d.kind, blob: d.blob, note: d.note, wanted: d.wanted, ...(d.answer ? { answer: d.answer } : {}), durationSec: d.durationSec, capturedAt: d.createdAt || undefined, ...(d.extra ? { extra: d.extra } : {}), ...(d.researchAttempt ? { researchAttempt: d.researchAttempt } : {}) };
}

export interface SendResult {
  completed: CompletedDraft[];
  /** The reason the run stopped at, when the server refused one. */
  blocked?: BlockedReason;
  failed: number;
  /** Stopped because there is no connection (nothing further was tried). */
  offline: boolean;
  /** Nothing was tried: nobody confirmed signed in yet. */
  skipped: boolean;
  /** Draft ids whose moments already existed and were found without a second create request. */
  reconciledIds: string[];
}

export interface SendOptions {
  /** Manual "Send all now": ask about each draft first, then let the server decide whether it is eligible. */
  force?: boolean;
  /** Just this draft. */
  only?: string;
}

let chain: Promise<unknown> = Promise.resolve();
let queuedAuto: Promise<SendResult> | null = null;

/**
 * Send what is due, oldest first, one at a time. Runs never overlap; an
 * automatic trigger that arrives while one is queued joins it.
 */
export function sendDrafts(opts: SendOptions = {}): Promise<SendResult> {
  const auto = !opts.force && !opts.only;
  if (auto && queuedAuto) return queuedAuto;
  const p = chain.then(() => {
    if (auto) queuedAuto = null;
    return run(opts);
  });
  if (auto) queuedAuto = p;
  chain = p.catch(() => undefined);
  return p;
}

/** Resolves once no send is running or queued. */
export function whenIdle(): Promise<void> {
  return chain.then(() => undefined);
}

/**
 * Ask the server what it holds for one draft's logical request, without
 * creating anything: `GET /api/moments/:clientId`. This is the safe way back
 * from an ambiguous acknowledgement — a request that was sent and whose
 * answer never arrived — because the id is the draft's own `clientId`, and a
 * moment made from it answers with that moment and no other.
 *
 * Anything the ask cannot settle on its own (an absent moment, a real
 * miscellaneous failure) returns null and leaves the send loop to settle it,
 * and every indefinite answer is read as "not sent": those are exactly the
 * cases where the send loop's own idempotent replay is what makes the moment.
 */
async function reconcile(d: Draft): Promise<Reconciliation | null> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { kind: 'offline', message: 'offline' };
  let res: Response;
  try {
    res = await fetch(`/api/moments/${encodeURIComponent(d.id)}`);
  } catch (e) {
    return reconcileOf(e);
  }
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => undefined) as { code?: string; reason?: string; error?: string } | undefined;
    return reconcileOf(new HttpError(res.status, body?.error ?? `HTTP ${res.status}`, body));
  }
  const m = await res.json().catch(() => undefined) as Moment | undefined;
  if (!m || typeof m.id !== 'string') return null;
  return { kind: 'sent', momentId: m.id };
}

/** Settle a draft whose answer may be missing, before sending it again. Never throws. */
async function reconcileBeforeSend(d: Draft): Promise<Reconciliation | null> {
  try {
    return await reconcile(d);
  } catch {
    return null;
  }
}

/** Raw Listen recordings return to the lantern's reveal before they can be sent. */
export function needsHearing(d: Draft): boolean {
  return d.kind === 'audio' && d.extra?.unheard === true;
}

async function run(opts: SendOptions): Promise<SendResult> {
  const out: SendResult = { completed: [], reconciledIds: [], failed: 0, offline: false, skipped: false };
  const s = session();
  if (s.status !== 'known' || !confirmed()) return { ...out, skipped: true };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ...out, offline: true };
  const owner = s.owner;
  let drafts: Draft[];
  try {
    // Over 30 days old (beta): removed here, never sent.
    drafts = await dropExpired(owner, await listOwn(owner));
  } catch {
    return out;
  }
  if (opts.only) drafts = drafts.filter((d) => d.id === opts.only);
  // A raw Listen clip always waits for explicit hearing, including manual and bulk retries.
  const candidates = drafts.filter((d) => (d.state === 'local' || d.state === 'blocked') && !needsHearing(d) && (opts.force || opts.only || !d.extra?.unheard));
  if (candidates.length === 0) return out;
  // One allowance read decides which blocked drafts could go now.
  let allowance: Allowance | undefined;
  if (!opts.force && candidates.some((d) => d.state === 'blocked')) {
    allowance = await api.allowance().catch(() => undefined);
  }
  const eligible = candidates.filter((d) => opts.force || opts.only || d.state === 'local' || cleared(d, allowance));

  try {
    for (let i = 0; i < eligible.length; i++) {
      const d = eligible[i];
      // The account changed under us (sign-in elsewhere in this browser): stop.
      if (currentOwner() !== owner || !confirmed()) break;
      if (s.beta && d.target && s.target && d.target !== s.target) {
        // Never relabelled: a draft stays in the language it was captured for.
        await patchDraft(d.id, (x) => ({ ...x, state: 'failed', failReason: 'draft-language', blockedReason: undefined }));
        out.failed++;
        continue;
      }
      // An ambiguous acknowledgement, or a manual send: ask the server what it
      // holds for this draft's own id before sending, so the run is about the
      // moment it may already hold rather than a second one made beside it.
      // (An automatic run of a draft with nothing unclear about it goes
      // straight to the create request: it is idempotent by the same id.)
      if (opts.force || opts.only || d.ambiguous) {
        const known = await reconcileBeforeSend(d);
        if (currentOwner() !== owner || !confirmed()) break;
        if (known?.kind === 'sent') {
          try {
            await removeRow(d.id);
            uncertainIds.delete(d.id);
          } catch {
            uncertainIds.add(d.id);
            await patchDraft(d.id, (x) => ({ ...x, state: 'local', ambiguous: true })).catch(() => undefined);
          }
          const done: CompletedDraft = { id: d.id, owner, kind: d.kind, momentId: known.momentId };
          completed.push(done);
          out.completed.push(done);
          out.reconciledIds.push(d.id);
          continue;
        }
        if (known?.kind === 'removed') {
          // Made before and deleted since: nothing to send, nothing to recreate.
          try {
            await removeRow(d.id);
            uncertainIds.delete(d.id);
          } catch {
            uncertainIds.add(d.id);
          }
          const gone: CompletedDraft = { id: d.id, owner, kind: d.kind, removed: true };
          completed.push(gone);
          out.completed.push(gone);
          continue;
        }
        if (known?.kind === 'expired') {
          await removeRow(d.id).catch(() => undefined);
          countExpired(owner, d.id);
          continue;
        }
        if (known?.kind === 'failed') {
          await patchDraft(d.id, (x) => ({ ...x, state: 'failed', failReason: known.reason, lastError: known.message, blockedReason: undefined, ambiguous: undefined }));
          out.failed++;
          continue;
        }
        if (known?.kind === 'session') {
          // A 401 says nothing about whether the earlier create succeeded.
          // Keep the uncertainty until this owner can ask again after signing in.
          break;
        }
        if (known?.kind === 'offline' && opts.only) {
          // "Send now" with no connection: nothing was sent and the draft stays.
          out.offline = true;
          break;
        }
        if (known?.kind === 'blocked') {
          await patchDraft(d.id, (x) => ({ ...x, state: 'blocked', blockedReason: known.reason, resetsAt: known.resetsAt, lastError: undefined, ambiguous: undefined })).catch(() => undefined);
          uncertainIds.delete(d.id);
          out.blocked = known.reason;
          break;
        }
      }
      sendingId = d.id;
      publish({ ...view, sendingId });
      let result: Moment | undefined;
      let err: unknown;
      try {
        result = await sendCapture(captureOf(d), s);
      } catch (e) {
        err = e;
      } finally {
        sendingId = null;
      }
      if (result) {
        try {
          await removeRow(d.id);
          uncertainIds.delete(d.id);
        } catch {
          // The server accepted this logical request, but the local row could
          // not be removed. Retain it with an explicit uncertain state; its
          // stable id makes a later retry safe too.
          uncertainIds.add(d.id);
          await patchDraft(d.id, (x) => ({ ...x, state: 'local', ambiguous: true })).catch(() => undefined);
        }
        const done: CompletedDraft = { id: d.id, owner, kind: d.kind, momentId: result.id, ...(d.researchAttempt ? { researchAttempt: d.researchAttempt } : {}) };
        completed.push(done);
        out.completed.push(done);
        continue;
      }
      const o = outcomeOf(err);
      const tried = (x: Draft): Draft => ({ ...x, attempts: x.attempts + 1, ambiguous: undefined });
      if (o.kind === 'blocked') {
        // Same reason for everything behind it: nothing else is sent until it lifts.
        for (const rest of eligible.slice(i)) {
          await patchDraft(rest.id, (x) => ({
            ...(rest.id === d.id ? tried(x) : x),
            state: 'blocked', blockedReason: o.reason, resetsAt: o.resetsAt, lastError: undefined,
          }));
        }
        out.blocked = o.reason;
        break;
      }
      if (o.kind === 'removed') {
        // Made before and deleted since: nothing to send, nothing to recreate.
        await removeRow(d.id);
        const gone: CompletedDraft = { id: d.id, owner, kind: d.kind, removed: true };
        completed.push(gone);
        out.completed.push(gone);
        continue;
      }
      if (o.kind === 'expired') {
        // The server's clock says it is past 30 days: it goes, like one ours caught.
        await removeRow(d.id);
        countExpired(owner, d.id);
        continue;
      }
      if (o.kind === 'failed') {
        if (o.reason === 'draft-account') {
          // The server has another account signed in than we thought: find out
          // who, and leave this draft for its owner.
          const now = await resolveSession();
          if (now.status !== 'known' || now.owner !== owner) break;
        }
        await patchDraft(d.id, (x) => ({ ...tried(x), state: 'failed', failReason: o.reason, lastError: o.message, blockedReason: undefined }));
        out.failed++;
        continue;
      }
      if (o.kind === 'session') {
        // Signed out meanwhile: the draft waits, hidden, for its owner.
        await patchDraft(d.id, tried);
        break;
      }
      if (o.kind === 'offline') {
        // Nothing went out (no connection), so nothing is unclear: the draft
        // keeps its local row and skips reconciliation. Only a request sent
        // with its answer lost (transient) stays ambiguous.
        await patchDraft(d.id, (x) => ({ ...tried(x), state: 'local', lastError: o.message, blockedReason: undefined, resetsAt: undefined, ambiguous: undefined })).catch(() => undefined);
        uncertainIds.delete(d.id);
        out.offline = true;
        break;
      }
      // Transient: a request went out and its answer was lost or the server stumbled.
      // The server may hold the moment already, so the row stays marked ambiguous:
      // the next run asks about that very id before it sends anything.
      await patchDraft(d.id, (x) => ({ ...tried(x), state: 'local', lastError: o.message, blockedReason: undefined, resetsAt: undefined, ambiguous: true })).catch(() => undefined);
      uncertainIds.add(d.id);
    }
  } finally {
    sendingId = null;
    await notify();
  }
  if (out.completed.some((c) => c.momentId) && typeof window !== 'undefined') {
    // Views listen for this: the record screen opens the lesson it was
    // holding, the home screen refreshes its list and allowance.
    window.dispatchEvent(new CustomEvent<CompletedDraft[]>('drafts-sent', { detail: out.completed }));
  }
  return out;
}

let wired = false;

/**
 * Call once at startup: list and send for whoever is signed in, again
 * whenever that changes, and whenever the connection comes back.
 */
export function ensureDrafts(): void {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  onSession(() => {
    void notify();
    if (confirmed()) void sendDrafts();
  });
  window.addEventListener('online', () => {
    void resolveSession().then(() => {
      if (confirmed()) void sendDrafts();
    });
  });
  void notify();
}
